import React from 'react'
import {
  composeRenderProps,
  LabelProps as AriaLabelProps,
  Switch as AriaSwitch,
  SwitchProps as AriaSwitchProps
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { SelectableLabel, SelectableSupportiveText } from './Selectable'

export interface SwitchProps extends AriaSwitchProps {}

const switchStyles = tv({
  base: 'group flex gap-2 items-center transition cursor-pointer',
  variants: {
    align: {
      top: 'items-start',
      middle: 'items-center'
    },
    isDisabled: {
      true: 'cursor-default',
      false: ''
    }
  }
})

const track = tv({
  base: 'flex items-center shrink-0 rounded-xl transition duration-200 ease-in-out shadow-inner border border-transparent',
  variants: {
    size: {
      xs: 'h-3 w-5',
      s: 'h-4 w-7',
      m: 'h-6 w-11'
    },
    isSelected: {
      false: 'bg-neutral-50 border-neutral-300',
      true: 'bg-highlight-600 group-pressed:border-brand-800'
    },
    isDisabled: {
      false:
        'group-focus:outline-none group-focus:outline-4 group-focus:outline-highlight-100 group-focus:outline-offset-0',
      true: 'group-focus:outline-0'
    }
  },
  compoundVariants: [
    {
      isSelected: true,
      isDisabled: true,
      className: 'bg-neutral-300 border-neutral-300 text-neutral-50'
    },
    {
      isSelected: false,
      isDisabled: true,
      className: 'bg-neutral-100 border-neutral-300 text-neutral-50'
    },
    {
      isSelected: false,
      isDisabled: false,
      className:
        'border-neutral-300 group-hover:border-brand-300 hover:bg-neutral-50 group-focus:border-brand-600'
    },
    {
      isSelected: true,
      isDisabled: false,
      className: 'hover:bg-highlight-700'
    },
    {
      isSelected: true,
      size: 'xs',
      className: 'px-0.5 py-0.5'
    },
    {
      isSelected: false,
      size: 'xs',
      className: 'p-0.5'
    },
    {
      isSelected: true,
      size: 's',
      className: 'px-1 py-0.5'
    },
    {
      isSelected: false,
      size: 's',
      className: 'p-0.5'
    },
    {
      isSelected: true,
      size: 'm',
      className: 'p-0.5'
    },
    {
      isSelected: false,
      size: 'm',
      className: 'px-1 py-0'
    }
  ]
})

const handle = tv({
  base: 'transform bg-neutral-0 rounded-full shadow transition duration-200 ease-in-out cursor-pointer',
  variants: {
    size: {
      xs: '',
      s: '',
      m: ''
    },
    isSelected: {
      false: 'translate-x-0',
      true: ''
    },
    isDisabled: {
      true: 'cursor-default',
      false: ''
    }
  },
  compoundVariants: [
    {
      isSelected: false,
      isDisabled: false,
      className:
        'bg-neutral-400 group-hover:bg-highlight-500 group-focus:bg-highlight-500'
    },
    {
      isSelected: true,
      size: 'xs',
      className: 'w-2.5 h-2.5 translate-x-[55%]'
    },
    {
      isSelected: false,
      size: 'xs',
      className: 'w-1.5 h-1.5'
    },
    {
      isSelected: true,
      size: 's',
      className: 'w-3 h-3 translate-x-[80%]'
    },
    {
      isSelected: false,
      size: 's',
      className: 'w-3 h-3'
    },
    {
      isSelected: true,
      size: 'm',
      className: 'w-5 h-5 translate-x-[95%]'
    },
    {
      isSelected: false,
      size: 'm',
      className: 'w-4 h-4'
    },
    {
      isDisabled: true,
      isSelected: false,
      className: 'bg-neutral-300'
    }
  ]
})

function Switch({
  children,
  size = 'm',
  ...props
}: SwitchProps & {
  size?: 'xs' | 's' | 'm'
  align?: 'top' | 'middle'
}) {
  return (
    <AriaSwitch
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        switchStyles({
          ...renderProps,
          align: props.align,
          isDisabled: props.isDisabled
        })
      )}>
      {renderProps => (
        <>
          <div
            className={track({
              ...renderProps,
              size
            })}>
            <span
              className={handle({
                ...renderProps,
                size
              })}
            />
          </div>

          {!!children && (
            <div className={`flex flex-col gap-1 text-left`}>
              {typeof children === 'function'
                ? children(renderProps)
                : children}
            </div>
          )}
        </>
      )}
    </AriaSwitch>
  )
}

function SwitchLabel(props: AriaLabelProps & { isDisabled?: boolean }) {
  return <SelectableLabel {...props} />
}

function SwitchSupportiveText(
  props: AriaLabelProps & { isDisabled?: boolean }
) {
  return <SelectableSupportiveText {...props} />
}

Switch.Label = SwitchLabel
Switch.SupportiveText = SwitchSupportiveText

export default Switch
